import React from "react";
import { TableColumnType, Typography, Dropdown, Tag, Space } from "antd";
import { AnyObject } from "antd/es/_util/type";
import {
  MenuOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import { MenuProps } from "antd";
import { Platform } from "../../types/platform";
import { SdkCategory } from "../../types/sdk";
import { Requirement } from "../../types/requirement";
import { PlatformIcon } from "./PlatformIcon";

const { Text } = Typography;

export enum DropdownAction {
  VIEW = "view",
  EDIT = "edit",
  COPY = "copy",
  DELETE = "delete",
}

// Shared tag renderers — kept standalone (not only table `render` callbacks) so pages such as
// MappingViewPage can reuse the exact same colors/labels outside of a Table column.
export const categoryTag = (text?: string) =>
  text === SdkCategory.MEDIATION ? (
    <Tag color="green">Mediation</Tag>
  ) : text === SdkCategory.AD_NETWORK ? (
    <Tag color="blue">Ad Network</Tag>
  ) : text === SdkCategory.SERVICE ? (
    <Tag color="orange">Service</Tag>
  ) : text ? (
    <Tag>{text}</Tag>
  ) : (
    <Text type="secondary">—</Text>
  );

export const requirementTag = (text?: string) =>
  text === Requirement.DEFAULT ? (
    <Tag color="geekblue">Default</Tag>
  ) : text === Requirement.REQUIRED ? (
    <Tag color="purple">Required</Tag>
  ) : text === Requirement.OPTIONAL ? (
    <Tag color="magenta">Optional</Tag>
  ) : text === Requirement.DEPRECATED ? (
    <Tag color="volcano">Deprecated</Tag>
  ) : text ? (
    <Tag>{text}</Tag>
  ) : (
    <Text type="secondary">—</Text>
  );

const adTypeTag = (type: string, index: number) => {
  switch (type) {
    case "banner":
      return <Tag key={index} color="blue">B</Tag>;
    case "interstitial":
      return <Tag key={index} color="green">I</Tag>;
    case "rewarded_video":
      return <Tag key={index} color="orange">R</Tag>;
    case "mrec":
      return <Tag key={index} color="purple">M</Tag>;
    case "native":
      return <Tag key={index} color="magenta">N</Tag>;
    default:
      return <Tag key={index}>{type}</Tag>;
  }
};

export const adTypeTags = (types?: string[]) =>
  types && types.length > 0 ? (
    <Space size={[0, 8]} direction="horizontal">
      {types.map((type, index) => adTypeTag(type, index))}
    </Space>
  ) : (
    <Text type="secondary">—</Text>
  );

interface ColumnBuilder {
  id: TableColumnType<AnyObject>;

  text: (
    title: string,
    dataIndex: string,
    strong?: boolean
  ) => TableColumnType<AnyObject>;

  dropdown: (
    actions: DropdownAction[],
    action: (record: number, action: DropdownAction) => void
  ) => TableColumnType<AnyObject>;

  version: TableColumnType<AnyObject>;

  category: (dataIndex: string) => TableColumnType<AnyObject>;

  platform: (dataIndex: string) => TableColumnType<AnyObject>;

  boolean: (title: string, dataIndex: string) => TableColumnType<AnyObject>;

  requirement: (title: string, dataIndex: string) => TableColumnType<AnyObject>;

  adType: (title: string, dataIndex: string) => TableColumnType<AnyObject>;

  array: (title: string, dataIndex: string) => TableColumnType<AnyObject>;

  date: (title: string, dataIndex: string) => TableColumnType<AnyObject>;
}

export const DefaultColumnBuilder: ColumnBuilder = {
  id: {
    title: "ID",
    ellipsis: true,
    width: "80px",
    dataIndex: "id",
    render: (text: number) => <Text type="secondary"># {text}</Text>,
    sorter: (a, b) => a.id - b.id,
    sortDirections: ["descend", "ascend"],
  },

  text: (title: string, dataIndex: string, strong?: boolean) => ({
    title: title,
    ellipsis: true,
    dataIndex: dataIndex,
    filters: [{ text: "Existed", value: "true" }],
    render: (text: number) => <Text strong={strong}>{text}</Text>,
    onFilter: (value, record) =>
      value === "true"
        ? record[dataIndex] && record[dataIndex].length > 0
        : true,
    sorter: (a, b) => (a[dataIndex] > b[dataIndex] ? 1 : -1),
    sortDirections: ["descend", "ascend"],
  }),

  dropdown: (
    actions: DropdownAction[],
    action: (record: number, action: DropdownAction) => void
  ) => {
    const items: MenuProps["items"] = actions.map((action) => {
      switch (action) {
        case DropdownAction.EDIT:
          return {
            key: DropdownAction.EDIT.toString(),
            label: "Edit",
            icon: <EditOutlined  />,
          };
        case DropdownAction.COPY:
          return {
            key: DropdownAction.COPY.toString(),
            label: "Copy",
            icon: <CopyOutlined  />,
          };
        case DropdownAction.DELETE:
          return {
            key: DropdownAction.DELETE.toString(),
            danger: true,
            label: "Delete",
            icon: <DeleteOutlined  />,
          };
        case DropdownAction.VIEW:
          return {
            key: DropdownAction.VIEW.toString(),
            label: "View",
            icon: <EyeOutlined  />,
          };
      }
    });

    return {
      dataIndex: "key",
      width: "44px",
      fixed: "right",
      // Opening the burger menu must not also trigger the row's onClick navigation.
      onCell: () => ({
        onClick: (e: React.MouseEvent) => e.stopPropagation(),
      }),
      render: (id: number) => {
        const onClick: MenuProps["onClick"] = ({ key }) => {
          action(id, key as DropdownAction);
        };

        return (
          <Dropdown menu={{ items, onClick }}>
            <MenuOutlined  />
          </Dropdown>
        );
      },
    };
  },

  version: {
    title: "Version",
    dataIndex: "version",
    render: (text: string) => <Text keyboard>{text}</Text>,
  },

  category: (dataIndex: string) => ({
    title: "Category",
    key: "category",
    dataIndex: dataIndex,
    filterMultiple: false,
    filters: [
      { text: "Mediation", value: "mediation" },
      { text: "Ad Network", value: "ad_network" },
      { text: "Service", value: "service" },
      { text: "Other", value: "other" },
    ],
    onFilter: (value, record) => record[dataIndex] === value,
    render: (text: string) => categoryTag(text),
  }),

  platform: (dataIndex: string) => ({
    title: "OS",
    key: "platform",
    width: "80px",
    dataIndex: dataIndex,
    filterMultiple: false,
    filters: [
      { text: "iOS", value: "ios" },
      { text: "Android", value: "android" },
    ],
    onFilter: (value, record) => record[dataIndex] === value,
    render: (text: string) => <PlatformIcon platform={text}/>
  }),

  boolean: (title: string, dataIndex: string) => ({
    title: title,
    key: dataIndex,
    width: "100px",
    dataIndex: dataIndex,
    filterMultiple: false,
    filters: [
      { text: `✅`, value: true },
      { text: `❌`, value: false },
    ],
    onFilter: (value, record) => record[dataIndex] === value,
    render: (text?: boolean) => {
      return <Text>{text === true ? `✅` : `❌`}</Text>;
    },
  }),

  requirement: (title: string, dataIndex: string) => ({
    title: title,
    dataIndex: dataIndex,
    filterMultiple: false,
    filters: [
      { text: "Default", value: "default" },
      { text: "Required", value: "required" },
      { text: "Optional", value: "optional" },
      { text: "Deprecated", value: "deprecated" },
    ],
    onFilter: (value, record) => record[dataIndex] === value,
    render: (text: string) => requirementTag(text),
  }),

  adType: (title: string, dataIndex: string) => ({
    title: title,
    width: "250px",
    dataIndex: dataIndex,
    filters: [
      { text: "Banner", value: "banner" },
      { text: "Interstitial", value: "interstitial" },
      { text: "Rewarded Video", value: "rewarded_video" },
      { text: "MREC", value: "mrec" },
      { text: "Native", value: "native" },
    ],
    onFilter: (value, record) =>
      Array.isArray(record[dataIndex]) ? record[dataIndex].includes(value) : false,
    render: (text: string[]) => adTypeTags(text),
  }),

  array: (title: string, dataIndex: string) => ({
    title: title,
    dataIndex: dataIndex,
    render: (text: string[]) => text.join(", ").substring(0, 100) + "...",
  }),

  date: (title: string, dataIndex: string) => ({
    title: title,
    dataIndex: dataIndex,
    render: (text: string) => new Date(text).toLocaleDateString(),
  }),
};
